import React, { useEffect, useState, useCallback } from "react";
import MUIDataTable from "mui-datatables";
import {
  Button,
  IconButton,
  DialogContent,
  Dialog,
  DialogActions,
  TextField,
  Box,
  Switch,
} from "@mui/material";
import { toast } from "react-toastify";
import { Close, DeleteOutline, Edit } from "@mui/icons-material";
import Axios from "../axios";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAuth } from "../auth/authContext";
import { findNavItemByPath } from "../navigation/navDataUtils";

const TagManagement = () => {
  const { navData } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [opendel, setOpendel] = useState({ status: false, id: null });
  const [openEdit, setOpenEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  const [name, setName] = useState("");

  const [allRows, setAllRows] = useState([]);
  const [write, setWrite] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  useEffect(() => {
    const currentPath = location.pathname;
    const matchingNavItem = findNavItemByPath(navData, currentPath);
    setWrite(Boolean(matchingNavItem?.write));
  }, [location.pathname, navData]);

  const fetchTags = useCallback(() => {
    Axios.get(`${BACKEND_URL}tag`)
      .then((res) => {
        setAllRows(res?.data?.data?.docs || []);
      })
      .catch((err) => showError(err));
  }, [BACKEND_URL]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTags();
  }, [fetchTags]);

  const showError = (err) => {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Something went wrong";
    toast.error(msg);
  };

  const resetForm = () => {
    setName("");
    setEditId(null);
  };

  const normalizeTagName = (value) => String(value || "").trim().toLowerCase();

  const hasDuplicateName = (value, excludedId = null) => {
    const normalized = normalizeTagName(value);
    return allRows.some(
      (row) =>
        normalizeTagName(row?.name) === normalized &&
        (!excludedId || String(row?._id) !== String(excludedId))
    );
  };

  const openEditDialog = (row) => {
    setEditId(row._id);
    setName(row.name || "");
    setOpenEdit(true);
  };

  const buildPayload = () => {
    const payload = {
      name: name.trim(),
    };

    return payload;
  };

  const addTag = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (hasDuplicateName(name)) {
      toast.error("Tag name already exists");
      return;
    }

    Axios.post(`${BACKEND_URL}tag`, buildPayload())
      .then((res) => {
        fetchTags();
        setOpen(false);
        resetForm();
        toast.success(res?.data?.message || "Tag added");
      })
      .catch((err) => showError(err));
  };

  const updateTag = () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (hasDuplicateName(name, editId)) {
      toast.error("Tag name already exists");
      return;
    }

    Axios.put(`${BACKEND_URL}tag/${editId}`, buildPayload())
      .then((res) => {
        fetchTags();
        setOpenEdit(false);
        resetForm();
        toast.success(res?.data?.message || "Tag updated");
      })
      .catch((err) => showError(err));
  };

  const deleteTag = () => {
    Axios.delete(`${BACKEND_URL}tag/${opendel.id}`)
      .then((res) => {
        fetchTags();
        setOpendel({ status: false, id: null });
        toast.success(res?.data?.message || "Deleted successfully");
      })
      .catch((err) => showError(err));
  };

  const toggleTagStatus = (id, currentActive) => {
    const endpoint = currentActive
      ? Axios.patch(`${BACKEND_URL}tag/${id}/deactivate`)
      : Axios.patch(`${BACKEND_URL}tag/${id}/activate`);

    endpoint
      .then((res) => {
        fetchTags();
        toast.success(res?.data?.message || "Status updated");
      })
      .catch((err) => showError(err));
  };

  const columns = [
    {
      name: "name",
      label: "Name",
    },
    write && {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={Boolean(allRows[i]?.is_active)}
              onChange={() => toggleTagStatus(allRows[i]?._id, allRows[i]?.is_active)}
              size="small"
            />
            <Edit
              sx={{ color: "#1976d2", fontSize: 20, cursor: "pointer" }}
              onClick={() => openEditDialog(allRows[i])}
            />
            <DeleteOutline
              sx={{ color: "red", fontSize: 20, cursor: "pointer" }}
              onClick={() => setOpendel({ status: true, id: allRows[i]?._id })}
            />
          </Box>
        ),
      },
    },
  ].filter(Boolean);

  const options = {
    responsive: "standard",
    selectableRows: "none",
    filter: false,
    search: true,
    viewColumns: false,
    rowsPerPageOptions: [],
    download: false,
  };

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Tags</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Tags</h2>
        {write && (
          <Button
            variant="contained"
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            Add Tag
          </Button>
        )}
      </div>

      <div className="table-responsive">
        <MUIDataTable
          title="Tags list"
          data={allRows}
          columns={columns}
          options={options}
        />
      </div>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Add tag</h3>
          <IconButton onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>
          <TextField
            label="Name"
            variant="outlined"
            required
            fullWidth
            className="my-2"
            onChange={(e) => setName(e.target.value)}
            value={name}
          />
        </DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button className="submit-button" variant="contained" onClick={addTag}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          resetForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Edit tag</h3>
          <IconButton
            onClick={() => {
              setOpenEdit(false);
              resetForm();
            }}
          >
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>
          <TextField
            label="Name"
            variant="outlined"
            required
            fullWidth
            className="my-2"
            onChange={(e) => setName(e.target.value)}
            value={name}
          />
        </DialogContent>
        <DialogActions>
          <Button
            className="cancel-button"
            onClick={() => {
              setOpenEdit(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button className="submit-button" variant="contained" onClick={updateTag}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={opendel.status}
        onClose={() => setOpendel({ status: false, id: null })}
        fullWidth
      >
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Delete tag</h3>
          <IconButton onClick={() => setOpendel({ status: false, id: null })}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>
          Are you sure you want to delete this tag?
        </DialogContent>
        <DialogActions>
          <Button
            className="cancel-button"
            onClick={() => setOpendel({ status: false, id: null })}
          >
            Cancel
          </Button>
          <Button className="submit-button" variant="contained" onClick={deleteTag}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TagManagement;
